import * as React from 'react';

import { CardShellActionButtons, type CardShellAction } from './CardShellActionButtons.js';
import {
  DimeCard,
  DimeCardAction,
  DimeCardDescription,
  DimeCardFooter,
  DimeCardHeader,
  DimeCardTitle,
} from './DimeCard.js';
import { cn } from './lib/utils.js';

type DimeMetricCardShellProps = Readonly<
  Omit<React.ComponentProps<typeof DimeCard>, 'children' | 'title'> & {
    /** The figure's label; also names the card region. */
    title: React.ReactNode;
    /** Any node under the title; styled as the card's figure, which the node's own classes override. */
    description: React.ReactNode;
    /** Supporting lines under the figure, such as its trend. */
    footer?: React.ReactNode;
    actions?: CardShellAction[];
  }
>;

/** A KPI card: a muted label over a large figure, optional supporting lines, and icon actions. */
export function DimeMetricCardShell({
  title,
  description,
  footer,
  actions = [],
  className,
  ...props
}: DimeMetricCardShellProps) {
  const titleId = React.useId();
  return (
    <DimeCard
      aria-labelledby={titleId}
      role="region"
      className={cn('@container/metric-card min-w-0 gap-1 py-3', className)}
      {...props}
    >
      <DimeCardHeader className="has-data-[slot=card-action]:grid-cols-[1fr_auto]">
        <DimeCardTitle id={titleId} className="text-xs font-normal text-muted-foreground">
          {title}
        </DimeCardTitle>
        <DimeCardDescription className="col-span-full text-lg font-semibold text-foreground tabular-nums [overflow-wrap:anywhere] @[13rem]/metric-card:text-xl @[250px]/metric-card:text-2xl">
          {description}
        </DimeCardDescription>
        {actions.length > 0 ? (
          <DimeCardAction className="col-start-2 row-span-1 row-start-1 flex h-4 items-center gap-1 self-start justify-self-end">
            <CardShellActionButtons actions={actions} />
          </DimeCardAction>
        ) : null}
      </DimeCardHeader>
      {footer ? (
        <DimeCardFooter className="flex-col items-start gap-0.5 pt-0 border-t-0 bg-transparent text-xs text-muted-foreground">
          {footer}
        </DimeCardFooter>
      ) : null}
    </DimeCard>
  );
}
